/**
 * @fileoverview Market page — runner opportunities, ratings/value performance,
 * crowd ownership trends, jockey/trainer performance, and virtual ROI.
 *
 * Data loading lives in services/marketService.js; metric transforms live in
 * utils/marketAnalytics.js — this component only orchestrates tab state and
 * renders the four tab views.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MarketHeader } from '../components/market/MarketHeader';
import { MarketTabs } from '../components/market/MarketTabs';
import { MarketEmptyState, MarketSkeletonBlock } from '../components/market/MarketEmptyState';
import { MarketOverviewTab } from '../components/market/MarketOverviewTab';
import { MarketLiveTab } from '../components/market/MarketLiveTab';
import { MarketPerformanceTab } from '../components/market/MarketPerformanceTab';
import { MarketCrowdTab } from '../components/market/MarketCrowdTab';
import { loadMarketData } from '../services/marketService';
import { buildMarketOverview } from '../utils/marketAnalytics';
import './MarketPage.css';

export const MarketPage = () => {
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await loadMarketData();
      setData(result);
    } catch (err) {
      console.error('[MarketPage] loadMarketData failed:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const overview = useMemo(
    () => buildMarketOverview(data?.runners || [], data?.slates || []),
    [data]
  );

  const hasAnyData = !!data && (data.runners || []).length > 0;

  return (
    <div className="mk-page">
      <MarketHeader kpis={overview.kpis} generatedAt={data?.generatedAt} loading={loading} />
      <MarketTabs active={tab} onChange={setTab} />

      {error ? (
        <MarketEmptyState
          icon="⚠️"
          title="Market insights could not be loaded"
          message="Please try again."
        />
      ) : loading ? (
        <div className="mk-tab-content">
          <MarketSkeletonBlock height={120} />
          <MarketSkeletonBlock height={260} />
          <MarketSkeletonBlock height={260} />
        </div>
      ) : !hasAnyData ? (
        <MarketEmptyState
          icon="📈"
          title="No market data yet"
          message="Market data will appear once more races are settled."
        />
      ) : (
        <>
          {tab === 'overview' && <MarketOverviewTab overview={overview} loading={loading} />}
          {tab === 'live' && <MarketLiveTab rows={data.runners} slates={data.slates} loading={loading} />}
          {tab === 'performance' && <MarketPerformanceTab rows={data.runners} loading={loading} />}
          {tab === 'crowd' && <MarketCrowdTab rows={data.runners} loading={loading} />}
        </>
      )}

      {data?.aggregatesErrored && !loading && (
        <p className="mk-partial-warning">
          Some ownership and return data couldn't be loaded — figures below may be incomplete.
        </p>
      )}
    </div>
  );
};

export default MarketPage;
